import React, { useState } from "react";
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  TextField, 
  InputAdornment,
  Box,
  Divider,
  Link,
  Button
} from "@mui/material";
import { SearchOutlined as SearchIcon, CalculateOutlined as CalculateIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import calculators, { allCalculatorsUrl } from "../utils/qxmd/calculatorsData";
import CalculatorDialog from "../../components/CalculatorDialog";
import _ from "lodash";

const Calculators = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCalculator, setSelectedCalculator] = useState(null);

  const filteredCalculators = searchTerm.length >= 3
    ? calculators.filter(calc => {
        // Primero filtrar por categoría si está seleccionada
        const categoryMatch = !selectedCategory || (Array.isArray(calc.categories) && calc.categories[0] === selectedCategory);
        
        // Luego filtrar por texto de búsqueda
        const textMatch = (typeof calc.name === 'string' && calc.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (typeof calc.description === 'string' && calc.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (Array.isArray(calc.categories) && calc.categories.some(cat => typeof cat === 'string' && cat.toLowerCase().includes(searchTerm.toLowerCase()))) ||
          (Array.isArray(calc.tags) && calc.tags.some(tag => typeof tag === 'string' && tag.toLowerCase().includes(searchTerm.toLowerCase())));
        
        return categoryMatch && textMatch;
      })
    : selectedCategory
    ? calculators.filter(calc => Array.isArray(calc.categories) && calc.categories[0] === selectedCategory)
    : calculators;

  const handleCalculatorClick = (id) => {
    const calculator = calculators.find(calc => calc.id === id);
    setSelectedCalculator(calculator);
  };
  
  const handleFilterByCategory = (category) => {
    setSelectedCategory(category);
  };

  const handleCloseCalculatorDialog = () => {
    setSelectedCalculator(null);
  };

  // Agrupar calculadoras por categoría principal
  const calculatorsByCategory = filteredCalculators.reduce((acc, calculator) => {
    const mainCategory = calculator.categories[0]; // Usamos la primera categoría como principal
    // Solo agregar si la categoría tiene nombre
    if (mainCategory && mainCategory.trim() !== '') {
      if (!acc[mainCategory]) {
        acc[mainCategory] = [];
      }
      acc[mainCategory].push(calculator);
    }
    return acc;
  }, {});

  const renderCalculators = () => {
    return Object.entries(calculatorsByCategory).map(([category, categoryCalculators]) => (
        <Box key={category} sx={{ mb: 4 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2, color: "primary.main" }}>
            {category}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            {(selectedCategory === category 
              ? categoryCalculators 
              : categoryCalculators.slice(0, 3)
            ).map((calculator) => (
              <Grid item xs={12} sm={6} md={4} key={calculator.id}>
                <Card 
                  sx={{ 
                    height: "100%", 
                    cursor: "pointer",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 3
                    }
                  }}
                  onClick={() => handleCalculatorClick(calculator.id)}
                >
                  <CardContent>
                    <Typography variant="subtitle1" component="div" fontWeight="bold" gutterBottom>
                      {calculator.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {_.size(calculator.categories) > 1 && calculator.categories[1]}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {categoryCalculators.length > 3 && !searchTerm && !selectedCategory && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={() => handleFilterByCategory(category)}
                sx={{ mt: 1 }}
              >
                Ver más ({categoryCalculators.length - 3})
              </Button>
            </Box>
          )}
        </Box>
      ));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: "flex", alignItems: "center" }}>
        <CalculateIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
        <Typography variant="h4" component="h1">
          Calculadoras médicas
        </Typography>
      </Box>
      
      <Typography variant="body1" paragraph>
        Accedé a calculadoras médicas para ayudarte en tu práctica clínica
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar calculadoras..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" color="text.secondary">
          Calculadoras por <Link href={allCalculatorsUrl} target="_blank" rel="noopener noreferrer">Calculator by QxMD</Link>*
        </Typography>
        
        {selectedCategory && (
          <Box sx={{ my: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => setSelectedCategory("")}
              startIcon={<ArrowBackIcon />}
            >
              Volver a todas las categorías
            </Button>
            <Typography variant="h6" sx={{ mt: 1, color: "primary.main" }}>
              Mostrando: {selectedCategory}
            </Typography>
          </Box>
        )}
        
        {renderCalculators()}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ paddingY: 4, display: "block", textAlign: "center" }}>
        *Esta herramienta redirige a un recurso externo (QxMD.com). Recetario no tiene afiliación con QxMD ni garantiza la precisión del contenido
      </Typography>
      
      <CalculatorDialog 
        calculator={selectedCalculator}
        onClose={handleCloseCalculatorDialog}
      />
    </Container>
  );
};

export default Calculators;
